// Потенциальные клиенты (лиды Bitrix). ТОЛЬКО ЧТЕНИЕ.
//
// НА КАКОЙ ВОПРОС ОТВЕЧАЕТ ЭКРАН
// «Кто к нам обратился, откуда пришёл, кому назначен и чем кончилось». Лид — заявка до
// превращения в сделку; до этого экрана их смотрели только в самом Bitrix. Источник —
// ЗЕРКАЛО (таблица bx_leads ядра): раздел отвечает, когда портал лежит, и переживёт
// его отключение.
//
// ПРАВИЛО ВЛАДЕЛЬЦА: СТАРЫЕ ДАННЫЕ — ИСТОРИЯ, ПОКАЗЫВАЕМ КАК ЕСТЬ
// Оговорки на экране можно, исправления — нет. Поэтому телефон «+7-911-…» и «+79604…»
// печатаются как записаны, статус из прошлой настройки Bitrix показывается кодом, а
// неровности называются оговорками, которые считает ядро.
//
// ЧТО ЭКРАН ОБЯЗАН СКАЗАТЬ ВСЛУХ, ИНАЧЕ ОН СОВРЁТ (тексты и числа считает ядро)
//   • ГРАНИЦА СИНКА. Лиды переносятся с даты, которую назвал владелец; более ранних в
//     зеркале нет вовсе. Это не фильтр — снять его с экрана нельзя.
//   • ДЕНЕГ НЕТ. Поле суммы у лидов не заполняют (ноль у всех строк — это «не ведут»,
//     а не «лиды на ноль рублей»), поэтому колонки суммы на экране нет вовсе.
//   • ФАЙЛОВ НЕТ. Вложения лида в зеркало не переносятся ни содержимым, ни ссылкой.
//     Карточка называет ЧИСЛОМ, сколько файловых полей вырезано, — молчание читалось
//     бы как «зеркало потеряло».
//   • ИСТОЧНИК «НЕ УКАЗАН» — состояние, а не ошибка: его можно выбрать фильтром.
//
// ЗАПИСИ ЗДЕСЬ НЕТ И НЕ БУДЕТ БЕЗ ОТДЕЛЬНОГО РЕШЕНИЯ ВЛАДЕЛЬЦА: на полях лида в Bitrix
// висят бизнес-процессы, которые ставят задачи живым людям.
//
// ТРАНСПОРТ ЖИВЁТ В ЭТОМ ЖЕ ФАЙЛЕ, а не в api/leads.ts: раздел читает четыре ручки
// одного модуля ядра, и другим экранам его хуки не нужны. Понадобятся (карточка
// компании захочет «лиды этой компании») — вынести в api/leads.ts станет отдельным
// осознанным шагом, а не археологией.
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, pluralN } from '../lib/format'

const БАЗА = '/api/leads'

/** Сколько строк на странице. То же число, что у соседних разделов зеркала. */
const РАЗМЕР_СТРАНИЦЫ = 50

/** Тот же разбор ответа, что в api/docs.ts. Продублирован намеренно: общего
 *  «api/base.ts» в проекте нет, и вводить его ради двадцати строк значило бы
 *  поставить всем разделам общую точку отказа. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Потенциальные клиенты» вам не открыт')
    }
    // 400 от ядра — неверный фильтр. Пустая таблица читалась бы как «лидов нет»,
    // а за этим идут выводы о продажах.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. leads_api.py) ───────────────

interface Контакт {
  value: string
  type: string | null
}

interface ЛидСтрока {
  bitrix_id: number
  title: string | null
  name: string | null
  second_name: string | null
  last_name: string | null
  person: string | null
  company_title: string | null
  post: string | null
  status_id: string | null
  status_semantic_id: string | null
  source_id: string | null
  source_description: string | null
  has_phone: boolean | null
  has_email: boolean | null
  phones: Контакт[]
  emails: Контакт[]
  assigned_by_id: number | null
  assigned_name: string | null
  company_id: number | null
  contact_id: number | null
  date_create: string | null
  date_modify: string | null
  date_closed: string | null
  last_activity_time: string | null
}

interface Список {
  rows: ЛидСтрока[]
  total: number
  next: number | null
  limit: number
  start: number
  sort: string
  since: string
}

interface СтатусСправочника {
  status_id: string
  name: string | null
  sort: number | null
  semantics: string | null
  color: string | null
  leads: number
}

interface ИсточникСправочника {
  source_id: string | null
  name: string | null
  leads: number
}

interface Ответственный {
  bitrix_id: number | null
  name: string | null
  leads: number
}

interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

interface Справочники {
  source: string
  since: string
  statuses: СтатусСправочника[]
  sources: ИсточникСправочника[]
  assigned: Ответственный[]
  semantics: Record<string, string>
  counts: Record<string, number | string | null>
  freshness: Свежесть[]
  sort_list: string[]
  data_notes: Record<string, string>
  caveats: string[]
}

interface ПолеКарточки {
  code: string
  value: unknown
}

interface Карточка {
  lead: ЛидСтрока & {
    comments: string | null
    opportunity: number | null
    is_return_customer: boolean | null
    web: Контакт[]
    created_by_id: number | null
    moved_time: string | null
    originator_id: string | null
    origin_id: string | null
    utm_source: string | null
    utm_medium: string | null
    utm_campaign: string | null
    utm_content: string | null
    utm_term: string | null
    synced_at: string | null
  }
  status_name: string | null
  semantic_name: string | null
  source_name: string | null
  fields: ПолеКарточки[]
  hidden_files: { count: number; why: string }
  since: string
}

interface СтрокаСтатуса {
  status_id: string | null
  name: string | null
  semantic: string | null
  semantic_name: string | null
  leads: number
  first_at: string | null
  last_at: string | null
}

interface Месяц {
  month: string
  leads: number
  converted: number
  junk: number
}

interface СтрокаИсточника {
  source_id: string | null
  name: string | null
  leads: number
  converted: number
}

interface Сводка {
  totals: { leads: number; in_work: number; converted: number; junk: number; other: number }
  by_status: СтрокаСтатуса[]
  months: Месяц[]
  by_source: СтрокаИсточника[]
  since: string
}

// ─────────────────────────────── хуки ───────────────────────────────

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

function useСправочникиЛидов() {
  return useQuery<Справочники>({
    queryKey: ['leads', 'refs'],
    queryFn: () => запрос<Справочники>('/refs'),
    ...ОБЩЕЕ,
  })
}

function useЛиды(ф: Record<string, unknown>) {
  return useQuery<Список>({
    queryKey: ['leads', 'list', ф],
    queryFn: () => запрос('/list' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/** Одна карточка. Пока лид не выбран — не ходим вовсе, а не спрашиваем «ничего». */
function useКарточкаЛида(ид?: number) {
  return useQuery<Карточка>({
    queryKey: ['leads', 'item', ид],
    queryFn: () => запрос(`/item?id=${ид}`),
    enabled: !!ид,
    ...ОБЩЕЕ,
  })
}

/** Сводка. `enabled` — вкладку «Сводка» открывают не всегда, зачем считать зря. */
function useСводкаЛидов(ф: Record<string, unknown>, enabled = true) {
  return useQuery<Сводка>({
    queryKey: ['leads', 'summary', ф],
    queryFn: () => запрос('/summary' + строкаЗапроса(ф)),
    enabled,
    ...ОБЩЕЕ,
  })
}

/** Как называется сущность зеркала в подписи «обновлено N мин назад». */
const ИМЯ_СУЩНОСТИ: Record<string, string> = { 'bxm:leads': 'лиды' }

// ─────────────────────────────── экран ───────────────────────────────

type Вкладка = 'list' | 'summary'

export default function Leads() {
  const [вкладка, setВкладка] = useState<Вкладка>('list')
  const refs = useСправочникиЛидов()

  const [статус, setСтатус] = useState('')
  const [ответственный, setОтветственный] = useState('')
  const [источник, setИсточник] = useState('')
  const [поиск, setПоиск] = useState('')
  const [сДаты, setСДаты] = useState('')
  const [поДату, setПоДату] = useState('')
  const [сортировка, setСортировка] = useState('created')
  const [start, setStart] = useState(0)

  const отбор = useMemo(() => ({
    status: статус || undefined,
    assigned: ответственный || undefined,
    source: источник || undefined,
    q: поиск.trim().length >= 2 ? поиск.trim() : undefined,
    created_from: сДаты || undefined,
    created_to: поДату || undefined,
  }), [статус, ответственный, источник, поиск, сДаты, поДату])

  const список = useЛиды({ ...отбор, sort: сортировка, start, limit: РАЗМЕР_СТРАНИЦЫ })
  // Сводка не зависит от поиска по строке — ядро его в сводке и не принимает.
  const { q: _q, ...отборСводки } = отбор
  const сводка = useСводкаЛидов(отборСводки, вкладка === 'summary')

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }
  const св = (refs.data?.freshness ?? []).find((с) => с.entity in ИМЯ_СУЩНОСТИ)

  return (
    <>
      <div className="page-head">
        <h1>Потенциальные клиенты</h1>
        <span className="spacer" />
        <span className="hint">
          {св?.age_min != null
            ? `${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity}: обновлено `
              + (св.age_min < 1 ? 'только что' : `${Math.round(св.age_min)} мин назад`)
            : 'свежесть неизвестна'}
        </span>
      </div>

      <Оговорки refs={refs.data} />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([['list', 'Реестр'], ['summary', 'Сводка']] as [Вкладка, string][])
          .map(([код, подпись]) => (
            <button key={код} onClick={() => setВкладка(код)}
                    aria-pressed={вкладка === код}
                    style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
              {подпись}
            </button>
          ))}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="leads-list">
          <div className="f">
            <label htmlFor="leads-status">Статус</label>
            <select id="leads-status" value={статус}
                    onChange={(e) => сСброса(setСтатус)(e.target.value)}>
              <option value="">все статусы</option>
              {(refs.data?.statuses ?? []).map((с) => (
                // Статус без имени показывается кодом, а не прячется: у старых лидов
                // живут статусы, которых в настройке Bitrix уже нет.
                <option key={с.status_id} value={с.status_id}>
                  {(с.name ?? с.status_id)} · {с.leads}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="leads-assigned">Ответственный</label>
            <select id="leads-assigned" value={ответственный}
                    onChange={(e) => сСброса(setОтветственный)(e.target.value)}>
              <option value="">все</option>
              {(refs.data?.assigned ?? []).map((о) => (
                <option key={о.bitrix_id ?? 'нет'} value={о.bitrix_id ?? ''}>
                  {(о.name || `#${о.bitrix_id}`)} · {о.leads}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="leads-source">Источник</label>
            <select id="leads-source" value={источник}
                    onChange={(e) => сСброса(setИсточник)(e.target.value)}>
              <option value="">все источники</option>
              {(refs.data?.sources ?? []).map((и) => (
                // «Не указан» — настоящее состояние, а не дыра: ядро принимает его
                // спец-значением none.
                <option key={и.source_id ?? 'none'} value={и.source_id ?? 'none'}>
                  {и.source_id === null ? 'не указан' : (и.name ?? и.source_id)} · {и.leads}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="leads-q">Название, компания или человек</label>
            <input id="leads-q" type="search" value={поиск} placeholder="Морвенна, Иванов…"
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="leads-from">Появился с</label>
            <input id="leads-from" type="date" value={сДаты}
                   onChange={(e) => сСброса(setСДаты)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="leads-to">по</label>
            <input id="leads-to" type="date" value={поДату}
                   onChange={(e) => сСброса(setПоДату)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="leads-sort">Порядок</label>
            <select id="leads-sort" value={сортировка}
                    onChange={(e) => сСброса(setСортировка)(e.target.value)}>
              <option value="created">когда появился</option>
              <option value="modified">когда трогали</option>
              <option value="activity">по последней активности</option>
            </select>
          </div>
        </FilterBar>
      </div>

      {вкладка === 'list' ? (
        <QueryState isLoading={список.isLoading} error={список.error}>
          <Таблица данные={список.data} refs={refs.data} />
          <Страницы всего={список.data?.total ?? 0} start={start} setStart={setStart} />
        </QueryState>
      ) : (
        <QueryState isLoading={сводка.isLoading} error={сводка.error}>
          {сводка.data && <СводкаЛидов с={сводка.data} />}
        </QueryState>
      )}
    </>
  )
}

// ═══════════════════════════════ оговорки ═══════════════════════════════
// Тексты и числа в них считает ядро. Своих чисел здесь нет ни одного намеренно: вбитая
// цифра однажды разойдётся с тем, что человек видит в таблице рядом.

function Оговорки({ refs }: { refs?: Справочники }) {
  const тексты = refs?.caveats ?? []
  if (!тексты.length) return null
  return (
    <div className="notice" style={{ marginBottom: 12 }}>
      <b>Что этот раздел показывает и чего в нём нет.</b>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
        {тексты.map((т, i) => <li key={i} style={{ marginBottom: 4 }}>{т}</li>)}
      </ul>
    </div>
  )
}

// ═══════════════════════════════ таблица ═══════════════════════════════

/** Имя статуса по коду — из справочника; код без имени показывается кодом. */
function имяСтатуса(refs: Справочники | undefined, код: string | null): string {
  if (!код) return '—'
  const с = (refs?.statuses ?? []).find((x) => x.status_id === код)
  return с?.name ?? код
}

function имяИсточника(refs: Справочники | undefined, код: string | null): string {
  // null — «не указан»: настоящее состояние, а не пропуск отрисовки.
  if (код === null) return 'не указан'
  const и = (refs?.sources ?? []).find((x) => x.source_id === код)
  return и?.name ?? код
}

/** Контакты строкой: первый плюс счётчик остальных. Значения — КАК ЕСТЬ. */
function контакты(список: Контакт[]): string {
  if (!список.length) return ''
  const первый = список[0].value
  return список.length > 1 ? `${первый} +${список.length - 1}` : первый
}

function Таблица({ данные, refs }: { данные?: Список; refs?: Справочники }) {
  const [открыт, setОткрыт] = useState<number | null>(null)
  const строки = данные?.rows ?? []

  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Лидов под этот отбор нет</div>
        <div className="empty-hint">
          Раздел показывает лиды, заведённые
          с {данные?.since ?? refs?.since ?? 'даты, которую назвал владелец'}: более
          ранние в зеркало не переносятся вовсе.
          Снимите фильтр по статусу или источнику — или расширьте период.
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Лид</th>
              <th>Кто обратился</th>
              <th>Контакты</th>
              <th>Статус</th>
              <th>Источник</th>
              <th>Ответственный</th>
              <th>Появился</th>
            </tr>
          </thead>
          <tbody>
            {строки.map((л) => (
              <tr key={л.bitrix_id}>
                <td data-label="Лид">
                  <button type="button" onClick={() => setОткрыт(л.bitrix_id)}>
                    {л.title || `#${л.bitrix_id}`}
                  </button>
                </td>
                <td data-label="Кто обратился">
                  {л.person || <span className="muted">имени нет</span>}
                  {л.post && <div className="muted">{л.post}</div>}
                  {л.company_title && (
                    <div className="muted">
                      {/* Ссылка — только когда лид ПРИВЯЗАН к компании зеркала;
                          голое название компании — текстом, как записано. */}
                      {л.company_id
                        ? <EntityLink kind="company" id={л.company_id} title={л.company_title} />
                        : л.company_title}
                    </div>
                  )}
                </td>
                <td data-label="Контакты">
                  {контакты(л.phones) || (л.has_phone
                    ? <span className="muted">телефон есть, но не доехал</span> : '')}
                  {л.phones.length > 0 && л.emails.length > 0 && <br />}
                  {контакты(л.emails)}
                  {!л.phones.length && !л.emails.length && !л.has_phone && !л.has_email
                    && <span className="muted">контактов нет</span>}
                </td>
                <td data-label="Статус">
                  {имяСтатуса(refs, л.status_id)}
                  {л.date_closed && <div className="muted">закрыт {fmtDate(л.date_closed)}</div>}
                </td>
                <td data-label="Источник">
                  {имяИсточника(refs, л.source_id)}
                  {л.source_description && <div className="muted">{л.source_description}</div>}
                </td>
                <td data-label="Ответственный">{л.assigned_name || '—'}</td>
                <td data-label="Появился">{fmtDate(л.date_create)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {открыт !== null && (
        <КарточкаЛида ид={открыт} закрыть={() => setОткрыт(null)} />
      )}
    </>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0}
              onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function КарточкаЛида({ ид, закрыть }: { ид: number; закрыть: () => void }) {
  const запрос = useКарточкаЛида(ид)
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>Лид #{ид}</b>
        <span className="spacer" />
        <button onClick={закрыть}>закрыть</button>
      </div>
      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {запрос.data && <ТелоКарточки к={запрос.data} />}
      </QueryState>
    </div>
  )
}

function ТелоКарточки({ к }: { к: Карточка }) {
  const л = к.lead
  const шапка: Array<[string, React.ReactNode]> = [
    ['Название', л.title || '—'],
    ['Кто обратился', [л.person, л.post].filter(Boolean).join(', ') || '—'],
    ['Компания', л.company_id
      ? <EntityLink kind="company" id={л.company_id} title={л.company_title} />
      : (л.company_title || '—')],
    ['Контакт в зеркале', л.contact_id
      ? <EntityLink kind="contact" id={л.contact_id} resolve />
      : <span className="muted">не привязан</span>],
    ['Статус', `${к.status_name ?? л.status_id ?? '—'}`
      + (к.semantic_name ? ` (${к.semantic_name})` : '')],
    ['Источник', (л.source_id === null ? 'не указан' : (к.source_name ?? л.source_id))
      + (л.source_description ? ` — ${л.source_description}` : '')],
    ['Ответственный', л.assigned_name || '—'],
    ['Появился', fmtDate(л.date_create, true)],
    ['Закрыт', л.date_closed ? fmtDate(л.date_closed, true) : '—'],
    ['Телефоны', л.phones.length
      ? л.phones.map((т) => `${т.value}${т.type ? ` (${т.type})` : ''}`).join(' · ')
      : '—'],
    ['Почта', л.emails.length ? л.emails.map((т) => т.value).join(' · ') : '—'],
    ['Сайт', л.web?.length ? л.web.map((т) => т.value).join(' · ') : '—'],
  ]
  const utm = [л.utm_source, л.utm_medium, л.utm_campaign, л.utm_content, л.utm_term]
    .filter(Boolean).join(' / ')

  return (
    <>
      <div className="hint" style={{ marginBottom: 8 }}>
        заявка до превращения в сделку · зеркало Bitrix, только чтение
        {л.synced_at ? ` · в зеркале с ${fmtDate(л.synced_at, true)}` : ''}
      </div>

      {/* Сколько файловых полей вырезано — ЧИСЛОМ. Молча вырезать нельзя: человек,
          который знает, что вложение в Bitrix есть, решит, что зеркало его потеряло. */}
      {к.hidden_files.count > 0 && (
        <div className="notice" style={{ marginBottom: 8 }}>
          <b>Файлов в этой карточке нет намеренно.</b> {к.hidden_files.why}
          {' '}Вырезано файловых полей у этого лида: {к.hidden_files.count}.
        </div>
      )}

      <div className="table-wrap cards" style={{ marginBottom: 12 }}>
        <table>
          <tbody>
            {шапка.map(([имя, значение]) => (
              <tr key={имя}>
                <td data-label="Поле" style={{ width: '30%' }}>{имя}</td>
                <td data-label="Значение">{значение}</td>
              </tr>
            ))}
            {utm && (
              <tr><td data-label="Поле">Метки UTM</td><td data-label="Значение">{utm}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {л.comments && (
        <>
          <b>Комментарий</b>
          <div className="card" style={{ margin: '8px 0 12px', whiteSpace: 'pre-wrap' }}>
            {/* Комментарий Bitrix бывает с html-тегами — печатаем ТЕКСТОМ как есть,
                не отрисовывая: чужой html на своём экране не исполняют. */}
            {л.comments}
          </div>
        </>
      )}

      <b>Остальные поля Bitrix</b>
      {к.fields.length ? (
        <>
          <div className="hint" style={{ margin: '4px 0 8px' }}>
            Человеческих названий у этих полей нет честно: справочник полей лида в
            зеркало не переносится, а выдумывать подписи по коду значило бы врать
            надписью. Значения — как записаны, включая нули и служебные коды.
          </div>
          <div className="table-wrap cards">
            <table>
              <thead><tr><th>Код поля</th><th>Значение</th></tr></thead>
              <tbody>
                {к.fields.map((п) => (
                  <tr key={п.code}>
                    <td data-label="Код поля"><code>{п.code}</code></td>
                    <td data-label="Значение">{показать(п.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="hint" style={{ marginTop: 8 }}>
          Кроме показанного выше, у этого лида ничего не заполнено.
        </div>
      )}
    </>
  )
}

/** Значение поля — в строку, КАК ЕСТЬ. Ноль печатается нулём: 0 == false в JS так же
 *  коварно, как в питоне, и «v || '—'» прятало бы живые нули. */
function показать(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'да' : 'нет'
  if (Array.isArray(v)) return v.map((x) => показать(x)).join(' · ')
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

// ═══════════════════════════════ сводка ═══════════════════════════════
// «В работе», «сконвертирован», «брак» — семантика самого Bitrix (P/S/F), а не наша
// классификация статусов: свой список пришлось бы догонять при каждом новом статусе.

function СводкаЛидов({ с }: { с: Сводка }) {
  if (!с.totals.leads) {
    return (
      <div className="card empty">
        <div className="empty-title">Под этот отбор не попал ни один лид</div>
        <div className="empty-hint">
          Сводка считается по тому же отбору, что и реестр (кроме строки поиска).
          Снимите фильтр по статусу или источнику — или расширьте период.
        </div>
      </div>
    )
  }
  return (
    <>
      <div className="tiles" style={{ marginBottom: 12 }}>
        <div className="tile">
          <div className="t-label">лидов под отбором</div>
          <div className="t-sum">{с.totals.leads}</div>
          <div className="t-label">с {с.since} — раньше в зеркале нет</div>
        </div>
        <div className="tile">
          <div className="t-label">в работе</div>
          <div className="t-sum">{с.totals.in_work}</div>
          <div className="t-label">семантика «в процессе» самого Bitrix</div>
        </div>
        <div className="tile">
          <div className="t-label">сконвертировано</div>
          <div className="t-sum">{с.totals.converted}</div>
          <div className="t-label">стали сделкой или контактом</div>
        </div>
        <div className="tile">
          <div className="t-label">брак</div>
          <div className="t-sum">{с.totals.junk}</div>
          <div className="t-label">
            некачественные и спам
            {с.totals.other > 0
              ? `; ещё ${pluralN(с.totals.other, 'лид', 'лида', 'лидов')} вне трёх семантик`
              : ''}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <b>По статусам</b>
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Статус</th><th>Семантика</th>
                <th className="num">Лидов</th><th>Последний</th>
              </tr>
            </thead>
            <tbody>
              {с.by_status.map((р) => (
                <tr key={р.status_id ?? 'нет'}>
                  <td data-label="Статус">{р.name ?? р.status_id ?? '—'}</td>
                  <td data-label="Семантика">{р.semantic_name ?? р.semantic ?? '—'}</td>
                  <td className="num" data-label="Лидов">{р.leads}</td>
                  <td data-label="Последний">{fmtDate(р.last_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <b>По месяцам</b>
        <div className="hint" style={{ marginTop: 4 }}>
          месяц — когда лид ПОЯВИЛСЯ; сконвертированные и брак считаются в месяце прихода
        </div>
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Месяц</th><th className="num">Пришло</th>
                <th className="num">Сконвертировано</th><th className="num">Брак</th>
              </tr>
            </thead>
            <tbody>
              {с.months.map((м) => (
                <tr key={м.month}>
                  <td data-label="Месяц">{м.month}</td>
                  <td className="num" data-label="Пришло">{м.leads}</td>
                  {/* Ноль печатается нулём: «в этом месяце не сконвертировали ничего» —
                      ответ, а не пропуск. */}
                  <td className="num" data-label="Сконвертировано">{м.converted}</td>
                  <td className="num" data-label="Брак">{м.junk}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <b>По источникам</b>
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Источник</th><th className="num">Лидов</th>
                <th className="num">Сконвертировано</th>
              </tr>
            </thead>
            <tbody>
              {с.by_source.map((и) => (
                <tr key={и.source_id ?? 'none'}>
                  <td data-label="Источник">
                    {и.source_id === null ? 'не указан' : (и.name ?? и.source_id)}
                  </td>
                  <td className="num" data-label="Лидов">{и.leads}</td>
                  <td className="num" data-label="Сконвертировано">{и.converted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
